import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useFeedback } from "../hooks/useFeedback";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import { Input } from "~/components/ui/input";
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "~/components/ui/dialog";
import {
  Field,
  FieldLabel,
  FieldContent,
  FieldGroup,
  FieldDescription,
  FieldSet,
  FieldLegend,
} from "~/components/ui/field";

interface FeedbackModalProps {
  inputText?: string;
  outputText?: string;
}

export function FeedbackModal({ inputText = "", outputText = "" }: FeedbackModalProps) {
  const { t } = useTranslation();
  const {
    selectedTypes,
    setSelectedTypes,
    details,
    setDetails,
    contact,
    setContact,
    isSubmitting,
    submit,
  } = useFeedback({ inputText, outputText });

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const success = await submit();
      if (success) {
        alert(t("feedback.submitSuccess"));
      }
    },
    [submit, t]
  );

  const toggleType = (type: string) => {
    setSelectedTypes(
      selectedTypes.includes(type)
        ? selectedTypes.filter((t: string) => t !== type)
        : [...selectedTypes, type]
    );
  };

  const checkboxOptions = [
    { value: "mistakes", label: t("feedback.mistakes") },
    { value: "letters", label: t("feedback.letters") },
    { value: "translit", label: t("feedback.translit") },
    { value: "exceptions", label: t("feedback.exceptions") },
    { value: "other", label: t("feedback.other") },
  ];

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{t("feedback.title")}</DialogTitle>
      </DialogHeader>
      <form onSubmit={handleSubmit} id="feedback-form">
        <FieldGroup>
          <FieldSet>
            {checkboxOptions.map((option) => (
              <Field key={option.value} orientation="horizontal">
                <Checkbox
                  id={option.value}
                  checked={selectedTypes.includes(option.value)}
                  onCheckedChange={() => toggleType(option.value)}
                />
                <FieldLabel htmlFor={option.value}>{option.label}</FieldLabel>
              </Field>
            ))}
          </FieldSet>

          <Field>
            <FieldLabel>{t("feedback.detailsLabel")}</FieldLabel>
            <textarea
              placeholder={t("feedback.detailsPlaceholder")}
              className="border border-input rounded-lg px-3 py-2 text-sm"
              rows={4}
              value={details}
              onChange={(e) => setDetails(e.target.value)}
            />
          </Field>

          <Field>
            <FieldLabel>{t("feedback.contactLabel")}</FieldLabel>
            <Input
              value={contact}
              onChange={(e) => setContact(e.target.value)}
            />
            <FieldDescription>{t("feedback.contactDescription")}</FieldDescription>
          </Field>
        </FieldGroup>
      </form>
      <DialogFooter>
        <Button type="submit" form="feedback-form" disabled={isSubmitting}>
          {t("feedback.send")}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
